import { useState } from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

import { LinkOut } from "@/components/cinch/LinkOut";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MarkdownView } from "@/lib/markdown";

import type { SearchHit } from "../api";

/**
 * One retrieved article — the best-matching chunk, with the article title
 * linking out to support.cinch.io. Shared by Search results and the Ask tab's
 * citations, so a citation and a search hit always read identically.
 *
 * The excerpt is collapsed by default: the ranking is the point of the list,
 * and eight open excerpts bury it.
 */
export function ArticleHit({
  hit,
  index,
  defaultOpen = false,
  onOpenArticle,
}: {
  hit: SearchHit;
  index?: number;
  defaultOpen?: boolean;
  /** Open the indexed article in the drawer (Articles tab owns the drawer). */
  onOpenArticle?: (documentId: string) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const crumbs = hit.heading_path.slice(1).join(" › ");
  const updated =
    hit.hubspot_updated_at?.slice(0, 10) ?? hit.crawl_date?.slice(0, 10);

  return (
    <div className="rounded-lg border">
      <div className="flex items-start gap-3 p-3">
        <Button
          variant="ghost"
          size="icon"
          className="mt-0.5 h-6 w-6 shrink-0"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Collapse excerpt" : "Expand excerpt"}
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            {index !== undefined ? (
              <span className="text-xs tabular-nums text-muted-foreground">
                [{index}]
              </span>
            ) : null}
            <LinkOut href={hit.url} className="text-sm">
              {hit.title ?? "Untitled article"}
            </LinkOut>
            {crumbs ? (
              <span className="text-xs text-muted-foreground">{crumbs}</span>
            ) : null}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge variant="secondary" className="tabular-nums">
              {hit.score.toFixed(3)}
            </Badge>
            {hit.category ? (
              <Badge variant="outline">{hit.category}</Badge>
            ) : null}
            {hit.is_internal ? <Badge variant="warning">internal</Badge> : null}
            <span className="text-xs text-muted-foreground">
              {updated ? `updated ${updated}` : "update date unknown"}
              {hit.chunk_index !== null && hit.chunk_count !== null
                ? ` · section ${hit.chunk_index + 1} of ${hit.chunk_count}`
                : ""}
            </span>
            {onOpenArticle ? (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => onOpenArticle(hit.document_id)}
              >
                view indexed article
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {open && hit.text ? (
        <div className="border-t p-4">
          <MarkdownView source={hit.text} className="text-sm" />
        </div>
      ) : null}
    </div>
  );
}
